import { datadogRum } from '@datadog/browser-rum';
import { addGlobalContexts, newTracker, setUserId } from '@snowplow/browser-tracker';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import I18n from 'i18next';
// eslint-disable-next-line import/no-named-as-default
import Cookies from 'cookies-ts';
import { environments } from '@/environment';
import { Env } from '@/types/environment';
import { redirectToPortalHome, redirectToPortalLogin } from '@/utilities/auth';

/*
  How many times we have handed the user to the portal login, and when we last
  did. Written right before we navigate, cleared once a health-check succeeds.

  The portal logs the user out before re-authenticating, so a stale cookie
  cannot loop on its own. A live IdP session re-issuing a token the API still
  rejects (revoked user, wrong audience, a staging cookie against the prod API)
  would. This bounds that to a couple of bounces plus an error screen.

  A cookie rather than sessionStorage: c360 already cannot work without cookies
  (the access token lives in one), so this guard is available in exactly the
  configurations where the app is. DOM storage is a separate axis that privacy
  extensions block on its own, which would leave the guard off and the loop open.

  Counting attempts rather than timing them: a pure cooldown only catches laps
  that complete inside the window. A slow lap — MFA, a sluggish IdP, a user
  typing a password — always looks "long enough ago" on return, so the guard
  re-arms every time and the loop never terminates. The window below only decides
  when to forgive past attempts, not whether to allow this one.
*/
const AUTH_RETRY_COOKIE = 'c360_auth_retry';

/* Two hand-offs is enough to tell a genuinely expired token (the first one fixes
   it) from one the API will keep rejecting however often we re-issue it. */
const MAX_AUTH_REDIRECTS = 2;

/* Attempts older than this are forgiven, so a session that hits the wall can try
   again later instead of being stuck until the tab closes. */
const AUTH_RETRY_WINDOW_MS = 10 * 60_000;

class Boot {
  public env: Env = 'dev';
  public axios: AxiosInstance = axios;

  /*
    Current axios interceptors is configured as: validateStatus: () => true ->  which will treat every status codes as successful.
    This config is on purpose at the beginning, Iker (no longer working at VC) implemented wrappers (requestHandler, responseHandler) to manipulate the response data/status manually.
    But, for example, an issue with this config is when using useMutation, onError will never be triggered (because every calls now are successful).
    Now, for not using custom wrappers (requestHandler, responseHandler), I created this mutationAxios to remove validateStatus logic in res interceptors.
    And to use useMutation naturally without any custom response manipulation.

    There're many places using current axios instance so it's a risk atm to update it directly.

    TODO: Review every React Query that are being used, analyze if we can remove validateStatus: () => true completely.
  */
  public mutationAxios: AxiosInstance = axios;

  private _authRedirectAbandoned = false;
  private _authRedirectInFlight = false;

  /**
   * Synchronous setup only. `healthCheck()` is deliberately not called here: its
   * result gates the first render, so React Query owns the call (see AuthGate).
   */
  public init(env: Env) {
    this.env = env;
    this.initLogger();
    this.initTracking();
    this.initApi();
  }

  private initLogger() {
    if (environments[this.env]?.datadog?.enabled) {
      datadogRum.init({
        applicationId: environments[this.env].datadog.config.applicationId,
        clientToken: environments[this.env].datadog.config.clientToken,
        env: environments[this.env].datadog.config.environment,
        premiumSampleRate: 0,
        replaySampleRate: 0,
        sampleRate: 100,
        service: environments[this.env].datadog.config.name,
        site: 'datadoghq.eu',
        trackInteractions: true,
        version: environments[this.env].datadog.config.version,
      });
    }
  }

  private initTracking() {
    if (environments[this.env]?.tracking?.enabled) {
      const user_id = this.getEmailForTracking();
      const globalCtx = [
        {
          schema: 'iglu:team.vctools/user/jsonschema/1-0-1',
          data: { user_id },
        },
        {
          schema: 'iglu:team.vctools/screen_context/jsonschema/1-0-3',
          data: {
            screen_name: 'c360',
          },
        },
      ];
      newTracker('spC360', environments[this.env].tracking.config.url, {
        appId: environments[this.env].tracking.config.appId,
        platform: environments[this.env].tracking.config.platform,
        discoverRootDomain: true,
        cookieName: '_spC360_',
        cookieSameSite: 'Lax',
        cookieSecure: true,
        sessionCookieTimeout: 3600 * 4,
      });
      addGlobalContexts(globalCtx);
      setUserId(user_id);
    }
  }

  private initApi() {
    const axiosConfig = {
      baseURL: environments[this.env].cstBaseUrl,
      headers: this.getHeaders(),
      timeout: 30_000,
    };

    this.axios = axios.create({
      ...axiosConfig,
      validateStatus: () => true, // validateData to prevent Axios throw when not 200 response
    });

    this.mutationAxios = axios.create({
      ...axiosConfig,
    });

    /*
      `this.axios` resolves every status (validateStatus above), so non-2xx
      arrives here rather than in the error arm below.
    */
    const onFulfilled = (response: AxiosResponse) => {
      if (response.status === 401) {
        this.handleUnauthenticated();
      }

      if (response.status === 403) {
        if (response.data.errorCode === undefined) {
          this.handleForbidden();
        }
        if (response.data.errorCode == 'READ_ONLY_PERMISSION_DENIED') {
          response.data.message = 'Access denied: You have read-only access';
        }
      }

      return response;
    };

    /*
      `mutationAxios` keeps axios' default validateStatus, so non-2xx rejects and
      never reaches onFulfilled — the auth handling has to be repeated here.
      Once `this.axios` is retired this is the only arm that remains.
    */
    const onRejected = (error: AxiosError<{ errorCode?: string }>) => {
      const status = error.response?.status;

      if (status === 401) {
        this.handleUnauthenticated();
      }

      if (status === 403 && error.response?.data?.errorCode === undefined) {
        this.handleForbidden();
      }

      return Promise.reject(error);
    };

    this.axios.interceptors.response.use(onFulfilled);
    this.mutationAxios.interceptors.response.use(onFulfilled, onRejected);
  }

  public async healthCheck(): Promise<AxiosResponse> {
    const res = await this.mutationAxios.get('/health-check');
    this.clearAuthRetry();
    return res;
  }

  /**
   * True when a 401 came back so soon after we handed the user to the portal
   * login that another redirect would only loop — the point at which we stop and
   * show an error instead. Stays false while a redirect is in flight, so the
   * caller can tell "navigating away" from "gave up".
   */
  public get authRedirectAbandoned(): boolean {
    return this._authRedirectAbandoned;
  }

  private handleUnauthenticated(): void {
    /*
      `location.replace()` does not halt execution — requests already in flight
      keep settling and re-entering here. Without this, one expired-token event
      with N parallel calls would spend N of the budget instead of the single
      hand-off it actually represents, and the next genuine 401 would find the
      guard already exhausted. The boot path is naturally safe (AuthGate holds
      rendering until the health-check settles, so only one request exists); this
      protects the mid-session path, where the app has many queries live.
    */
    if (this._authRedirectInFlight) {
      return;
    }

    const { attempts, at } = this.readAuthRetry();

    // A negative age means the clock moved backwards since we wrote the cookie,
    // so it is unknowable — treat it as outside the window and start the count
    // over. That self-heals off the corrected clock, where carrying the count
    // forward could strand the user on the error screen.
    const age = Date.now() - at;
    const withinWindow = at > 0 && age >= 0 && age < AUTH_RETRY_WINDOW_MS;
    const attempt = withinWindow ? attempts + 1 : 1;

    if (attempt > MAX_AUTH_REDIRECTS) {
      this._authRedirectAbandoned = true;
      return;
    }

    this._authRedirectInFlight = true;
    this.writeAuthRetry(attempt, Date.now());
    redirectToPortalLogin(this.env);
  }

  private handleForbidden(): void {
    redirectToPortalHome(this.env);
  }

  /** Stored as `attempts:epochMs`; zeroes when absent or unparseable. */
  private readAuthRetry(): { attempts: number; at: number } {
    const [attempts, at] = String(new Cookies().get(AUTH_RETRY_COOKIE) ?? '')
      .split(':')
      .map(Number);

    return {
      attempts: Number.isFinite(attempts) ? attempts : 0,
      at: Number.isFinite(at) ? at : 0,
    };
  }

  /* Explicit expiry: cookies-ts defaults to a day, and the cookie is meaningless
     once older than the window. No `domain`, so it stays host-only and is never
     sent to the API. */
  private writeAuthRetry(attempts: number, at: number): void {
    new Cookies().set(AUTH_RETRY_COOKIE, `${attempts}:${at}`, { expires: '1h' });
  }

  private clearAuthRetry(): void {
    new Cookies().remove(AUTH_RETRY_COOKIE);
  }

  public getHeaders(): Record<string, string> {
    return {
      'Accept-Language': `${I18n.language};q=0.9,${I18n.language};q=0.8,*;q=0.5`,
      Authorization: `Bearer ${this._getAccessToken()}`,
      'Content-Type': 'application/json',
    };
  }

  public getEmailForTracking(): string | null {
    try {
      const idToken = this._getIdToken();
      return idToken ? JSON.parse(window.atob(idToken.split('.')[1])).email : null;
    } catch {
      return null;
    }
  }

  private _getAccessToken(): string {
    const cookies = new Cookies();
    return `${cookies.get(environments[this.env].bearerName)}`;
  }

  private _getIdToken(): string {
    const cookies = new Cookies();
    return `${cookies.get(environments[this.env].idToken)}`;
  }
}

export const BootService = new Boot();
